import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// API functions for vendor bookings
export const vendorBookingApi = {
  // Get all vendor bookings
  getVendorBookings: async (params = {}) => {
    const response = await api.get('/vendor/bookings', { params });
    return response.data;
  },

  // Get vendor booking by ID
  getVendorBookingById: async (bookingId) => {
    const response = await api.get(`/vendor/bookings/${bookingId}`);
    return response.data;
  },

  // Update booking status
  updateBookingStatus: async ({ bookingId, status, reason }) => {
    const response = await api.put(`/vendor/bookings/${bookingId}/status`, {
      status,
      cancellation_reason: reason
    });
    return response.data;
  },

  // Get upcoming bookings
  getUpcomingBookings: async () => {
    const response = await api.get('/vendor/bookings/upcoming');
    return response.data;
  },

  // Get booking statistics
  getBookingStatistics: async () => {
    const response = await api.get('/vendor/bookings/statistics');
    return response.data;
  },

  // Add booking notes
  addBookingNotes: async ({ bookingId, notes }) => {
    const response = await api.post(`/vendor/bookings/${bookingId}/notes`, { notes });
    return response.data;
  },

  // Get booking timeline
  getBookingTimeline: async (bookingId) => {
    const response = await api.get(`/vendor/bookings/${bookingId}/timeline`);
    return response.data;
  },

  // Mark booking as completed
  markCompleted: async (bookingId) => {
    const response = await api.put(`/vendor/bookings/${bookingId}/complete`);
    return response.data;
  },

  // Reschedule booking
  rescheduleBooking: async ({ bookingId, data }) => {
    const response = await api.put(`/vendor/bookings/${bookingId}/reschedule`, data);
    return response.data;
  }
};

// React Query Hooks

// Get all vendor bookings
export const useVendorBookings = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['vendorBookings', params],
    queryFn: () => vendorBookingApi.getVendorBookings(params),
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    ...options
  });
};

// Get single vendor booking
export const useVendorBooking = (bookingId, options = {}) => {
  return useQuery({
    queryKey: ['vendorBooking', bookingId],
    queryFn: () => vendorBookingApi.getVendorBookingById(bookingId),
    enabled: !!bookingId,
    ...options
  });
};

// Get upcoming bookings
export const useUpcomingBookings = (options = {}) => {
  return useQuery({
    queryKey: ['vendorBookings', 'upcoming'],
    queryFn: vendorBookingApi.getUpcomingBookings,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    ...options
  });
};

// Get booking statistics
export const useBookingStatistics = (options = {}) => {
  return useQuery({
    queryKey: ['vendorBookings', 'statistics'],
    queryFn: vendorBookingApi.getBookingStatistics,
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    ...options
  });
};

// Update booking status mutation
export const useUpdateBookingStatus = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: vendorBookingApi.updateBookingStatus,
    onSuccess: (data) => {
      // Invalidate and refetch bookings
      queryClient.invalidateQueries({ queryKey: ['vendorBookings'] });
      queryClient.invalidateQueries({ queryKey: ['vendorBooking', data.data.id] });
      queryClient.invalidateQueries({ queryKey: ['vendorBookings', 'upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['vendorBookings', 'statistics'] });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Add booking notes mutation
export const useAddBookingNotes = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: vendorBookingApi.addBookingNotes,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendorBooking', data.data.booking_id] });
      queryClient.invalidateQueries({ queryKey: ['vendorBookings'] });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Mark booking as completed mutation
export const useMarkBookingCompleted = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: vendorBookingApi.markCompleted,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendorBookings'] });
      queryClient.invalidateQueries({ queryKey: ['vendorBooking', data.data.id] });
      queryClient.invalidateQueries({ queryKey: ['vendorBookings', 'upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['vendorBookings', 'statistics'] });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Reschedule booking mutation
export const useRescheduleBooking = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: vendorBookingApi.rescheduleBooking,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendorBookings'] });
      queryClient.invalidateQueries({ queryKey: ['vendorBooking', data.data.id] });
      queryClient.invalidateQueries({ queryKey: ['vendorBookings', 'upcoming'] });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};
